/**
 * theme/common.ts — the shared dressing toolkit every cache theme is built from.
 *
 * `ruin.ts` shipped first and stands ALONE, deliberately: it is the reference the
 * dressing contract was read off, and it is byte-frozen so the render gate's measured
 * ruin numbers stay comparable across this phase. This file is therefore an EXTRACTION
 * BY COPY of the parts of ruin.ts that generalise — the BFS floor ladder, the
 * corridor polyline network with its shared-cell height vote, the walkable-feature
 * height field (softened — see {@link WalkField.sample}), the chunk-cutting wall emitter, the wall→BoxSpec fold and the
 * silhouette visual helpers — rewritten as parameterised pieces for `vault.ts` and
 * `hollow.ts`. Nothing here imports ruin.ts and ruin.ts imports nothing here.
 *
 * THE CONTRACT. Every theme returns a {@link CacheDressing}: the heightfield the pad
 * host displaces its sphere with, the box field the character collides against, the
 * render-space scene content, and a `setGateOpen` rebuild hook. Same shape for all
 * three, so the lab (and, later, main.ts) switches theme with zero special cases.
 *
 * COLLIDERS ARE THE TRUTH. Themes may draw whatever they like, but every solid a
 * player can touch must exist as a {@link WallBox} that becomes a real box in the
 * {@link BoxColliderField}. There is ONE collider vocabulary — axis-aligned boxes with
 * a `standable` flag — across all three themes; a theme that seems to need a new shape
 * is a theme whose geometry needs redesigning (the plan's rule, not a preference).
 *
 * SPACE. Pad-local (x, z) metres throughout, with heights measured ABOVE THE BASE
 * SPHERE. Render placement always goes through `sphereY`, never flat `h` — see
 * padWorld.ts's header for why that law exists.
 */
import * as THREE from "three";
import {
  BoxColliderField,
  LIVE_SINK_EXTRA_M,
  type CacheGateBox,
  type CacheLayoutV1,
  type CachePropKind,
  type CacheWallBox,
  type Rng,
} from "@hyperlight/shared";

/**
 * ⚠ THE PLAN AND THE WALL EMITTERS NOW LIVE IN `shared/src/cache/walls.ts`.
 *
 * They were moved there verbatim (2026-07-27) because the SERVER needs them: a wall
 * that only the dressing knows about is a wall bolts fly through and drones shoot
 * through, which is exactly the bug that prompted the move. This file re-exports them
 * so every theme's import list — and every byte of its geometry — is unchanged.
 * `WallBox`/`CacheGate` are aliases of the shared box vocabulary, and the aliases are
 * the SAME structural type, so a theme may keep declaring its own (ruin.ts does).
 */
export {
  CacheWalkField as WalkField,
  LIVE_SINK_EXTRA_M,
  SIDE_DX,
  SIDE_DZ,
  buildBoxField,
  buildGates,
  corridorMidpoint,
  emitCorridorFlanks,
  emitRoomPerimeter,
  emitWallRun,
  localPoint,
  sideOf,
  wallSpec,
  type CorrFeat,
  type WalkSample,
  type WallRunOptions,
} from "@hyperlight/shared";

// ── the dressing contract ─────────────────────────────────────────────────────

/** @see CacheWallBox — the shared pad-local solid every theme emits. */
export type WallBox = CacheWallBox;

/** @see CacheGateBox — a gate barrier plus whether it currently stands. */
export type CacheGate = CacheGateBox;

/**
 * What every `dress*(layout)` returns. Structural, not nominal: `RuinDressing`
 * already satisfies it (the optional members are exactly the ones ruin has no use
 * for), which is what lets the lab hold one variable for all three themes.
 */
export interface CacheDressing {
  readonly layout: CacheLayoutV1;
  /** Pad sphere radius the geometry was built for. */
  readonly radius: number;
  /** Terrain height above the base sphere at pad-local (x, z) — feed to `makePadHost`. */
  heightAt(x: number, z: number): number;
  /** Render-space ground height (the sphereY law) — every visual sits on this. */
  sphereY(x: number, z: number): number;
  /** Current solid-box field. Replaced by {@link setGateOpen}. */
  colliders: BoxColliderField;
  /** Render-space scene content. */
  group: THREE.Group;
  /** Every static vertical box, in build order — the lab's collider overlay reads this. */
  readonly walls: readonly WallBox[];
  readonly gates: CacheGate[];
  /** Room floor heights, indexed by room. */
  readonly floorH: readonly number[];
  /** Overhead boxes (vault ceilings / hollow ceiling shell). Absent for open-air themes. */
  readonly ceilings?: readonly WallBox[];
  /**
   * Open/close a gate. Rebuilds the collider field (the field has no removal seam) and
   * returns the NEW one — hand it to `padHost.setColliders`.
   */
  setGateOpen(edgeIndex: number, open: boolean): BoxColliderField;
  dispose(): void;
}

export interface ThemeOptions {
  radius?: number;
  /** Build the THREE scene content. Off for headless sweeps (`group` stays empty). */
  visuals?: boolean;
  /**
   * LIVE-ON-A-PLANET mode (P6b). Default false — the lab and the render gate keep
   * the terraced look, and every measured number in `test:cacherender` is a
   * `flat: false` number.
   *
   * When a cache is played for real the dressing does NOT own the ground: it rides a
   * real {@link import("../../world/planet").Planet} built over the pad SystemDef, and
   * that planet's sampler is the surface the character stands on. Three consequences,
   * all from the same fact, all switched by this one flag:
   *
   *   1. **Floor terraces are zeroed.** The ladder itself is shared now
   *      (`CacheRoom.floorH`, P8), but the live GROUND is not: the player clamps to the
   *      pad planet's sampler and so do the drones (`DroneField`'s `SurfaceFn`), and
   *      neither knows about terraces. Honouring floorH live needs a cache floor field
   *      on both — see `liveCache.ts`'s header. Until then, terracing one side alone
   *      would put the player on one surface, the drones on another and the props on a
   *      third. Ramps become level; ridges, walls, ceilings and gates are unchanged
   *      because every one of them is measured RELATIVE to its floor.
   *   2. **The dressing's own ground mesh is replaced by a sunk backdrop plate**
   *      ({@link addUnderplate}). The planet already draws a ground and a second one
   *      interpenetrates it — but the planet's quadtree STREAMS, so something has to
   *      stand between the dressing and open sky until it arrives.
   *   3. **Solid boxes sink by {@link LIVE_SINK_EXTRA_M}** so the pad planet's ~1.2 m of
   *      sampler relief cannot open a gap under a wall — see that constant.
   *
   * TODO(P8+): with a cache floor field on both grounds this flag loses its first
   * meaning and keeps the other two — at which point it wants renaming to `live`.
   */
  flat?: boolean;
}

/** Sink for a theme's boxes: its own constant, plus the live-mode allowance. */
export function themeSink(baseSink: number, opts: ThemeOptions): number {
  return baseSink + (opts.flat === true ? LIVE_SINK_EXTRA_M : 0);
}

// ── small maths ───────────────────────────────────────────────────────────────

export const smooth01 = (t: number): number => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));


/** Seeded value noise on a torus — deterministic ridge/undulation detail. */
export function makeValueNoise(rng: Rng, amp: number, scale: number, g = 64): (x: number, z: number) => number {
  const nt = new Float64Array(g * g);
  for (let i = 0; i < nt.length; i++) nt[i] = rng() * 2 - 1;
  const wrap = (v: number): number => ((v % g) + g) % g;
  const nAt = (i: number, j: number): number => nt[wrap(j) * g + wrap(i)];
  return (x: number, z: number): number => {
    const u = x / scale, v = z / scale;
    const i0 = Math.floor(u), j0 = Math.floor(v);
    const su = smooth01(u - i0), sv = smooth01(v - j0);
    const a = nAt(i0, j0) + (nAt(i0 + 1, j0) - nAt(i0, j0)) * su;
    const b = nAt(i0, j0 + 1) + (nAt(i0 + 1, j0 + 1) - nAt(i0, j0 + 1)) * su;
    return (a + (b - a) * sv) * amp;
  };
}

// ── floor heights ─────────────────────────────────────────────────────────────

/**
 * Room floor heights: READ from the layout, which is where the ladder now lives.
 *
 * ⚠⚠ THIS FUNCTION STILL DRAWS FROM `rng`, AND MUST. The ladder used to be computed
 * here from the dresser's own stream, so its jitter was that stream's first n−1 draws;
 * `shared/src/cache/layout.ts`'s `roomFloorHeights` reproduces exactly those draws to
 * stay byte-equal. If the dresser stopped spending them, every LATER draw (noise
 * tables, per-room headroom, floor undulation) would shift by n−1 and every dungeon in
 * the game would re-roll. So the draws stay, deliberately unused — the same trick
 * {@link ThemeOptions.flat} already relies on.
 */
export function roomFloorHeights(layout: CacheLayoutV1, rng: Rng, opts: ThemeOptions): Float64Array {
  const n = layout.rooms.length;
  for (let i = 0; i < n; i++) if (i !== layout.entrance) rng(); // stream alignment — see above
  const floorH = new Float64Array(n);
  if (opts.flat === true) return floorH; // live mode: one datum for everybody
  for (let i = 0; i < n; i++) floorH[i] = layout.rooms[i].floorH;
  return floorH;
}

// ── visual helpers (silhouette-grade, shared by the geometry themes) ──────────

export interface VisualCtx {
  sphereY(x: number, z: number): number;
  heightAt(x: number, z: number): number;
  keep<T extends { dispose(): void }>(o: T): T;
}

/** Render-space Y of a box's CENTRE (its base sits on `baseH` above the sphere). */
export function boxRenderY(ctx: VisualCtx, b: WallBox): number {
  return ctx.sphereY(b.cx, b.cz) + b.baseH - ctx.heightAt(b.cx, b.cz) + b.height / 2;
}

/** The displaced ground plane, named for the render gate's feet-vs-visual raycast. */
export function addGroundMesh(
  group: THREE.Group, ctx: VisualCtx, layout: CacheLayoutV1, color: number,
): void {
  const size = 2 * layout.extent + 90;
  // ⚠ THE CAP IS A FIDELITY BOUND, NOT A PERF KNOB — it must leave the ~1.2 m target
  // spacing intact at the LARGEST layout, or the mesh silently coarsens as layouts grow.
  // That is exactly what the 2026-08-02 CELL_M scale-up did: extent 91 → 130 m pushed
  // `size` to 350, the old cap of 224 stretched the same grid to 1.56 m, and the
  // feet-vs-visual gate went 45 → 203 mm (the chord error across ruin's 4.5 m ridge
  // ramp goes as the square of the spacing). 320 restores 1.2 m at tier 1 with headroom;
  // raise it again alongside any further widening rather than relaxing the 5 cm gate,
  // which is a real "the floor you see is the floor you stand on" invariant.
  const seg = Math.max(48, Math.min(320, Math.round(size / 1.2)));
  const geo = ctx.keep(new THREE.PlaneGeometry(size, size, seg, seg));
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) pos.setY(i, ctx.sphereY(pos.getX(i), pos.getZ(i)));
  geo.computeVertexNormals();
  const mat = ctx.keep(new THREE.MeshStandardMaterial({ color, roughness: 0.98, flatShading: true }));
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "cacheGroundMesh"; // probe seam: feet-vs-visual-ground checks
  group.add(mesh);
}

/**
 * How far below the datum the live UNDERPLATE sits, metres. Must clear the host pad
 * planet's sampler relief (±1.2 m) so the plate can never poke up through the real
 * ground; 2 m does, with room to spare.
 */
export const LIVE_UNDERPLATE_M = 2;

/**
 * A flat backdrop plate for {@link ThemeOptions.flat} mode.
 *
 * Live, the HOST PLANET draws the ground — but its quadtree streams, and until the
 * tiles under the player mesh there is nothing between the dressing and open sky.
 * (Measured: on a software renderer that gap lasts many seconds, and a dungeon
 * floating over the starfield is the single worst thing a player can see on arrival.)
 * So flat mode swaps the theme's own heightfield ground for this: one plate, seated a
 * fixed {@link LIVE_UNDERPLATE_M} BELOW the datum — under the planet's relief
 * everywhere, so it fills the void while terrain arrives and then quietly disappears
 * beneath it. Flat, not the heightfield: ruin's ridges rise 7 m and would punch
 * straight through the real ground.
 */
export function addUnderplate(
  group: THREE.Group, ctx: VisualCtx, layout: CacheLayoutV1, color: number, radius: number,
): void {
  const size = 2 * layout.extent + 90;
  const geo = ctx.keep(new THREE.PlaneGeometry(size, size, 24, 24));
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  const rr = radius - LIVE_UNDERPLATE_M;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i);
    pos.setY(i, Math.sqrt(Math.max(0, rr * rr - x * x - z * z)) - radius);
  }
  geo.computeVertexNormals();
  const mat = ctx.keep(new THREE.MeshStandardMaterial({ color, roughness: 1, flatShading: true }));
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = "cacheUnderplate";
  group.add(mesh);
}

/** One instanced box per collider box — the picture cannot disagree with the collision. */
export function addBoxInstances(
  group: THREE.Group, ctx: VisualCtx, boxes: readonly WallBox[],
  name: string, mat: THREE.Material,
): void {
  if (boxes.length === 0) return;
  const geo = ctx.keep(new THREE.BoxGeometry(1, 1, 1));
  const inst = new THREE.InstancedMesh(geo, mat, boxes.length);
  const m = new THREE.Matrix4();
  boxes.forEach((b, i) => {
    const sx = b.along === "x" ? b.halfLen * 2 : b.halfT * 2;
    const sz = b.along === "x" ? b.halfT * 2 : b.halfLen * 2;
    m.makeScale(sx, b.height, sz);
    m.setPosition(b.cx, boxRenderY(ctx, b), b.cz);
    inst.setMatrixAt(i, m);
  });
  inst.instanceMatrix.needsUpdate = true;
  inst.name = name;
  group.add(inst);
}

/** Room floor slabs tinted by role — silhouette-level readability, not art. */
export function addRoomFloors(
  group: THREE.Group, ctx: VisualCtx, layout: CacheLayoutV1,
  tint: (role: CacheLayoutV1["rooms"][number]["role"]) => number,
  lift = 0.05,
): void {
  if (layout.rooms.length === 0) return;
  const geo = ctx.keep(new THREE.BoxGeometry(1, 0.08, 1));
  const mat = ctx.keep(new THREE.MeshStandardMaterial({ roughness: 0.9 }));
  const inst = new THREE.InstancedMesh(geo, mat, layout.rooms.length);
  const m = new THREE.Matrix4();
  const col = new THREE.Color();
  layout.rooms.forEach((r, i) => {
    m.makeScale(r.w, 1, r.d);
    m.setPosition(r.x, ctx.sphereY(r.x, r.z) + lift, r.z);
    inst.setMatrixAt(i, m);
    inst.setColorAt(i, col.setHex(tint(r.role)));
  });
  inst.instanceMatrix.needsUpdate = true;
  inst.name = "cacheRoomFloors";
  group.add(inst);
}

export interface PropStyle {
  color: number;
  emissive?: number;
  emissiveIntensity?: number;
  roughness?: number;
  metalness?: number;
}

/**
 * One simple marker per abstract prop kind, plus (capped) real PointLights at `light`
 * props. The cap is a perf rule, not a taste one: an interior theme with a light per
 * prop would put dozens of dynamic lights in one scene.
 */
export function addProps(
  group: THREE.Group, ctx: VisualCtx, layout: CacheLayoutV1,
  style: Record<CachePropKind, PropStyle>,
  light: { color: number; intensity: number; distance: number; max: number } | null,
): void {
  const geos: Record<CachePropKind, THREE.BufferGeometry> = {
    loot: ctx.keep(new THREE.OctahedronGeometry(0.55)),
    light: ctx.keep(new THREE.SphereGeometry(0.34, 10, 8)),
    cover: ctx.keep(new THREE.BoxGeometry(1.7, 1.1, 1.1)),
    hazard: ctx.keep(new THREE.ConeGeometry(0.7, 1.3, 6)),
    marker: ctx.keep(new THREE.CylinderGeometry(0.14, 0.14, 2.4, 6)),
  };
  const mats = {} as Record<CachePropKind, THREE.Material>;
  for (const k of Object.keys(style) as CachePropKind[]) {
    const s = style[k];
    mats[k] = ctx.keep(new THREE.MeshStandardMaterial({
      color: s.color,
      emissive: s.emissive ?? 0x000000,
      emissiveIntensity: s.emissiveIntensity ?? 1,
      roughness: s.roughness ?? 0.8,
      metalness: s.metalness ?? 0,
    }));
  }
  const lift: Record<CachePropKind, number> = { loot: 0.6, light: 1.9, cover: 0.6, hazard: 0.7, marker: 1.2 };
  let lit = 0;
  for (const p of layout.props) {
    const room = layout.rooms[p.room];
    const x = room.x + p.u, z = room.z + p.v;
    const mesh = new THREE.Mesh(geos[p.kind], mats[p.kind]);
    mesh.position.set(x, ctx.sphereY(x, z) + lift[p.kind], z);
    mesh.rotation.y = p.yaw;
    group.add(mesh);
    if (p.kind === "light" && light && lit < light.max) {
      lit++;
      const pl = new THREE.PointLight(light.color, light.intensity, light.distance);
      pl.position.copy(mesh.position);
      group.add(pl);
    }
  }
}

/** Entrance + boss markers — you must be able to find both from anywhere on the pad. */
export function addMarkers(
  group: THREE.Group, ctx: VisualCtx, layout: CacheLayoutV1,
  entrance: { color: number; emissive: number }, boss: { color: number; emissive: number },
): void {
  {
    const ent = layout.rooms[layout.entrance];
    const geo = ctx.keep(new THREE.CylinderGeometry(0.45, 0.7, 9, 8));
    const mat = ctx.keep(new THREE.MeshStandardMaterial({
      color: entrance.color, emissive: entrance.emissive, emissiveIntensity: 0.8, roughness: 0.5,
    }));
    const pillar = new THREE.Mesh(geo, mat);
    pillar.position.set(ent.x, ctx.sphereY(ent.x, ent.z) + 4.5, ent.z);
    pillar.name = "cacheEntranceMarker";
    group.add(pillar);
  }
  {
    const b = layout.rooms[layout.boss];
    const geo = ctx.keep(new THREE.BoxGeometry(1.1, 7, 1.1));
    const mat = ctx.keep(new THREE.MeshStandardMaterial({
      color: boss.color, emissive: boss.emissive, emissiveIntensity: 0.7, roughness: 0.85,
    }));
    const ring = new THREE.Group();
    const q = 0.42;
    for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      const x = b.x + sx * b.w * q, z = b.z + sz * b.d * q;
      const pillar = new THREE.Mesh(geo, mat);
      pillar.position.set(x, ctx.sphereY(x, z) + 3.5, z);
      ring.add(pillar);
    }
    ring.name = "cacheBossMarker";
    group.add(ring);
  }
}
